// app-subdomain-creators/src/stores/mediaUploadStore.js

import { defineStore } from 'pinia';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

/* -------------------------------------------------------
   UUID Helper
------------------------------------------------------- */
function uid() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now() + "-" + Math.random());
}

/* -------------------------------------------------------
   File Type Detection
------------------------------------------------------- */
function detectFileType(mimeType) {
  if (!mimeType) return 'other';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed') return 'zip';
  return 'other';
}

function detectFileTypeFromName(fileName) {
  const name = String(fileName || '').toLowerCase().trim();
  if (!name) return 'other';

  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/i.test(name)) return 'image';
  if (/\.(mp4|mov|m4v|webm|mkv|avi|ogv|3gp)$/i.test(name)) return 'video';
  if (/\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(name)) return 'audio';
  if (/\.pdf$/i.test(name)) return 'pdf';
  if (/\.(zip|rar|7z|tar|gz)$/i.test(name)) return 'zip';
  return 'other';
}

function getFileType(file) {
  const byMime = detectFileType(file?.type || '');
  if (byMime !== 'other') return byMime;
  return detectFileTypeFromName(file?.name || '');
}

function probeVideoDimensions(file) {
  return new Promise((resolve) => {
    if (!file || getFileType(file) !== 'video' || typeof document === 'undefined') {
      resolve({ width: 0, height: 0 });
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.muted = true;
    let settled = false;
    const finish = (width = 0, height = 0) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: Math.max(0, Number(width) || 0),
        height: Math.max(0, Number(height) || 0),
      });
    };
    probe.onloadedmetadata = () => finish(probe.videoWidth, probe.videoHeight);
    probe.onerror = () => finish();
    probe.src = objectUrl;
  });
}

/* -------------------------------------------------------
   STORE
------------------------------------------------------- */
export const useMediaUploadStore = defineStore('mediaUpload', {
  state: () => ({
    uploads: [],   // Each upload = { id, fileName, status, progress, error, fileType }
  }),

  getters: {
    activeUploads(state) {
      return state.uploads.filter(
        u => u.status === 'uploading' || u.status === 'processing'
      );
    },
    hasAnyUploads(state) {
      return state.uploads.length > 0;
    }
  },

  

  actions: {
    /* ---------------------------------------------------
       PERSISTENCE
    --------------------------------------------------- */
    persistQueue() {
      const safe = this.uploads.map(u => ({
        ...u,
        xhr: null,  // strip non-serializable reference
      }));
      localStorage.setItem("mediaUploadQueue", JSON.stringify(safe));
    },

    restoreQueue() {
      const saved = localStorage.getItem("mediaUploadQueue");
      if (!saved) return;

      try {
        const parsed = JSON.parse(saved);
        // Ensure no missing fields, keep stable identity
        this.uploads = parsed.map(u => ({
          id: u.id || uid(),
          fileName: u.fileName,
          status: u.status || 'uploading',
          progress: typeof u.progress === 'number' ? u.progress : 0,
          error: u.error || null,
          fileType: u.fileType || 'other',
        }));
      } catch (err) {
        console.error("Failed to restore queue:", err);
      }
    },

    /* ---------------------------------------------------
       ADD MULTIPLE FILES
    --------------------------------------------------- */
    async addFiles(files, folderId) {
      const arr = Array.from(files);
      for (const file of arr) {
        await this.uploadSingleFile(file, folderId);
      }
    },

/* ---------------------------------------------------
   UPLOAD A SINGLE FILE
--------------------------------------------------- */
async uploadSingleFile(file, folderId, options = {}) {
  const uploadId = uid();
  const fileType = getFileType(file);
  const mimeType = file.type || 'application/octet-stream';
  const durationCandidate = Number(options?.durationSeconds);
  const durationSeconds =
    Number.isFinite(durationCandidate) && durationCandidate > 0
      ? Math.round(durationCandidate)
      : null;
  const sourceDimensions = fileType === 'video'
    ? await probeVideoDimensions(file)
    : { width: 0, height: 0 };
  const onProgress =
    typeof options.onProgress === 'function' ? options.onProgress : null;
  const onError =
    typeof options.onError === 'function' ? options.onError : null;

  // where this upload came from; default "mediaLibrary"
  const origin = options.origin || 'mediaLibrary';
  const isFlixOrigin = String(origin || '').toLowerCase().startsWith('creator-flix-theatre-');
  const signedUrlPath = options.generateSignedUrlPath || (isFlixOrigin
    ? '/api/creator-flix-theatre/uploads/generate-signed-url'
    : '/api/generate-signed-url');
  const createRecordPath = options.createRecordPath || (isFlixOrigin
    ? '/api/creator-flix-theatre/uploads/create-record'
    : '/api/create-record');

  /* Push new upload item */
  this.uploads.push({
    id: uploadId,
    fileName: file.name,
    status: 'uploading',
    progress: 0,
    error: null,
    fileType,
    startTime: Date.now(),
    totalBytes: file.size,
    uploadedBytes: 0,
    speed: 0,
    etaSeconds: null,
    xhr: null,          // used for canceling
    fileId: null,       // meta filled later
    permanentUrl: null, // meta filled later
    origin,             // ðŸ‘ˆ where this upload came from
  });
  this.persistQueue();

  const getItem = () => this.uploads.find(u => u.id === uploadId);

  try {
    /* 1) Get signed URL */
    const signedRes = await fetch(`${apiProxyUrl}${signedUrlPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        filename: file.name,
        contentType: mimeType,
        fileType,
        sizeBytes: file.size,
        sourceWidth: sourceDimensions.width,
        sourceHeight: sourceDimensions.height,
        folderId: folderId || null,
      }),
    });

    if (!signedRes.ok) {
      const errorData = await signedRes.json().catch(() => ({}));
      const error = new Error(errorData?.message || `Failed to get signed URL: ${signedRes.status}`);
      error.code = errorData?.code;
      error.details = errorData?.details;
      throw error;
    }

    const { fileId, signedUrl, permanentUrl } = await signedRes.json();

    // Save IDs on the upload item
    const itemAfterSigned = getItem();
    if (itemAfterSigned) {
      itemAfterSigned.fileId = fileId;
      itemAfterSigned.permanentUrl = permanentUrl;
      this.persistQueue();
    }

    /* 2) Upload to S3 with progress */
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const item = getItem();
      if (item) item.xhr = xhr;   // store the actual upload request

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader(
        'Content-Type',
        file.type || 'application/octet-stream'
      );

      xhr.upload.onprogress = (event) => {
        const current = getItem();
        if (!current || !event.lengthComputable) return;

        const percent = Math.round((event.loaded / event.total) * 100);
        current.progress = percent;
        current.uploadedBytes = event.loaded;

        const elapsed = (Date.now() - current.startTime) / 1000; // seconds
        current.speed = event.loaded / elapsed;                  // bytes/second

        if (current.speed > 0) {
          const remainingBytes = current.totalBytes - current.uploadedBytes;
          current.etaSeconds = Math.round(remainingBytes / current.speed);
        }

        current.status = 'uploading';
        this.persistQueue();
        onProgress?.({
          uploadId,
          progress: current.progress,
          status: current.status,
          etaSeconds: current.etaSeconds,
          speed: current.speed,
        });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));

      xhr.send(file);
    });

    /* 3) Create DB media_files record */
    const recordRes = await fetch(`${apiProxyUrl}${createRecordPath}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        fileId,
        fileName: file.name,
        fileUrl: permanentUrl,
        fileType,
        mimeType,
        sizeBytes: file.size,
        folderId: folderId || null,
        durationSeconds,
        sourceWidth: sourceDimensions.width,
        sourceHeight: sourceDimensions.height,
        thumbnailUrl: null,
        origin,
      }),
    });

    if (!recordRes.ok) {
      const errorData = await recordRes.json().catch(() => ({}));
      const error = new Error(errorData?.message || `Failed to create media record: ${recordRes.status}`);
      error.code = errorData?.code;
      error.details = errorData?.details;
      throw error;
    }

    /* 4) Finalize upload state */
    const finalItem = getItem();
    if (finalItem) {
      finalItem.xhr = null;
      finalItem.etaSeconds = 0;

      finalItem.progress = 100;
      finalItem.status = 'done';
      finalItem.error = null;

      this.persistQueue();
      onProgress?.({
        uploadId,
        progress: 100,
        status: finalItem.status,
        etaSeconds: 0,
        speed: finalItem.speed,
      });
    }

    // ✅ Return richer metadata for callers (e.g. newsfeed composer)
    return {
      fileId,
      permanentUrl,
      fileType,
      mimeType,
      type: fileType,
      thumbnailUrl:
        fileType === 'video'
          ? `${apiProxyUrl}/api/video-thumbnail/${encodeURIComponent(String(fileId))}`
          : null,
      hlsPlaylistUrl:
        fileType === 'video'
          ? `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(String(fileId))}`
          : null,
      hlsStatus: fileType === 'video' ? 'pending' : null,
      durationSeconds,
    };

  } catch (err) {
    console.error("Upload error:", err);

    if (err?.code === 'creator_plan_limit_reached' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('creator-plan-limit-reached', {
        detail: {
          ...(err.details || {}),
          key: err.details?.limitKey || 'media_storage_bytes',
          label: err.details?.label || 'Media storage',
          publicLimit: err.details?.publicLimit ?? err.details?.limit,
          unit: err.details?.unit || 'bytes',
        },
      }));
    }

    try {
      onError?.({
        code: err?.code || '',
        message: err?.message || 'Upload failed',
        details: err?.details || {},
      });
    } catch (callbackError) {
      console.warn('Media upload error callback failed:', callbackError);
    }

    const erroredItem = getItem();
    if (erroredItem) {
      erroredItem.xhr = null;
      erroredItem.etaSeconds = null;

      erroredItem.status = "error";
      erroredItem.error = err.message || "Unknown error";

      this.persistQueue();
      onProgress?.({
        uploadId,
        progress: erroredItem.progress,
        status: erroredItem.status,
        etaSeconds: erroredItem.etaSeconds,
        speed: erroredItem.speed,
        error: erroredItem.error,
      });
    }

    // Let callers know it failed
    return null;
  }
},



    cancelUpload(id) {
      const item = this.uploads.find(u => u.id === id);
      if (!item) return;

      // Abort the underlying request
      if (item.xhr) {
        try {
          item.xhr.abort();
        } catch (e) {
          // swallow abort errors
        }
      }

      item.status = "canceled";
      item.error = "Upload canceled";
      item.progress = 0;
      item.etaSeconds = null;
      item.xhr = null;

      this.persistQueue();
    },


    /* ---------------------------------------------------
       CLEAR FINISHED UPLOADS
    --------------------------------------------------- */
    clearFinished() {
  // Remove everything that is NOT actively uploading or processing
  this.uploads = this.uploads.filter(u =>
    u.status === "uploading" ||
    u.status === "processing"
  );

  this.persistQueue();
}


  },
});

